/**
 * Current user (GET /api/me), shared by the whole app.
 */
import { useSyncExternalStore } from 'react';
import type { GraphFeatures, MeDto, Role } from '../../shared/api.js';
import { HttpError, api, setCsrfToken } from './api.js';
import { browser } from './navigation.js';

let me: MeDto | null = null;
/** Set when a 401 response says development login is available (details.devAuth). */
let devAuthHint = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export function setMe(next: MeDto | null): void {
  me = next;
  setCsrfToken(next?.csrfToken ?? null);
  emit();
}

export function getMe(): MeDto | null {
  return me;
}

/** Whether GET /api/me answered 401; remembers what that answer says about development login. */
function isUnauthorized(err: unknown): boolean {
  if (!(err instanceof HttpError) || err.status !== 401) return false;
  const details = err.body?.details as { devAuth?: unknown } | undefined;
  devAuthHint = details?.devAuth === true;
  return true;
}

/** Loads the current user; resolves to null when not logged in (401). */
export async function loadMe(): Promise<MeDto | null> {
  try {
    setMe(await api.get<MeDto>('/api/me'));
  } catch (err) {
    if (!isUnauthorized(err)) throw err;
    setMe(null);
  }
  return me;
}

/**
 * Re-checks the session after another request answered 401: resolves to the
 * current user (refreshed, e.g. with changed roles) or to null when the
 * session has really ended (the login page then knows whether to offer the
 * development logins). Throws when the server cannot be reached.
 */
export async function recheckSession(): Promise<MeDto | null> {
  try {
    const fresh = await api.get<MeDto>('/api/me');
    setMe(fresh);
    return fresh;
  } catch (err) {
    if (isUnauthorized(err)) return null;
    throw err;
  }
}

/** Whether the login page may offer the development logins (dev server, or the API said so). */
export function devLoginAvailable(): boolean {
  return devAuthHint || import.meta.env.DEV;
}

/** Ends the session on the server and navigates to the Microsoft sign-out page (or home). */
export async function logout(): Promise<void> {
  let redirect = '/';
  try {
    const res = await api.post<{ redirect?: string }>('/auth/logout');
    if (res?.redirect) redirect = res.redirect;
  } catch {
    // The session may already be gone; the user is logged out either way.
  }
  setMe(null);
  browser.assign(redirect);
}

export function subscribeMe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useMe(): MeDto | null {
  return useSyncExternalStore(subscribeMe, getMe, getMe);
}

const NO_FEATURES: GraphFeatures = { graphConfigured: false, directory: false, sharepoint: false, calendarSync: false, freeBusy: false };

/** Microsoft 365 features available on this server (all off without a session). */
export function useFeatures(): GraphFeatures {
  return useMe()?.features ?? NO_FEATURES;
}

const RANK: Record<Role, number> = { viewer: 1, planner: 2, admin: 3 };

/** admin implies planner implies viewer. */
export function hasRole(user: MeDto | null, role: Role): boolean {
  if (!user) return false;
  return user.user.roles.some((r) => RANK[r] >= RANK[role]);
}

/** The planning the user works in: personal choice, else the server default. */
export function planningIdOf(user: MeDto | null): string | null {
  return user?.settings.defaultPlanningId ?? user?.defaultPlanningId ?? null;
}
